import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, Sparkle, Copy, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import { LOGO_URL } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERKS = [
  "8 AI Twin videos every month",
  "Up to 60-second lip-synced reels",
  "No watermark — post as-is",
  "Full Auto Create + AI Twin Gallery access",
  "Cancel anytime from your Account Settings",
];

export default function WebinarPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const canceled = params.get("canceled") === "1";

  const [promo, setPromo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null); // "monthly" | "yearly" | null
  const [email, setEmail] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/webinar/promo`)
      .then((res) => setPromo(res.data))
      .catch(() => toast.error("Couldn't load promo details."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (canceled) toast.info("Checkout canceled — your offer is still here whenever you're ready.");
  }, [canceled]);

  const claim = async (plan) => {
    setClaiming(plan);
    try {
      const body = {
        plan,
        origin_url: window.location.origin,
      };
      // Anonymous visitors can pass an email — signed-in users skip this.
      const token = localStorage.getItem("session_token");
      if (!token && email) body.email = email;

      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
      const { data } = await axios.post(`${API}/webinar/checkout`, body, { headers });
      window.location.href = data.checkout_url;
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Checkout failed.";
      toast.error(msg);
      setClaiming(null);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`${code} copied.`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAFAFA] via-white to-pink-50" data-testid="webinar-page">
      <header className="h-16 bg-white border-b border-zinc-200 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20">
        <button onClick={() => navigate("/")} className="flex items-center" data-testid="webinar-home-link">
          <img src={LOGO_URL} alt="The Branding Bar" className="h-9 object-contain" />
        </button>
        <button
          onClick={() => navigate("/login")}
          data-testid="webinar-signin-btn"
          className="px-4 py-2 text-sm font-semibold text-[#E91E8C] hover:bg-[#E91E8C]/10 rounded-lg"
        >
          Sign in
        </button>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase px-3 py-1 rounded-full bg-[#E91E8C]/10 text-[#E91E8C]">
            <Sparkle size={14} weight="fill" /> Webinar exclusive
          </span>
          <h1 className="mt-4 text-4xl sm:text-5xl font-bold text-zinc-900 leading-tight">
            Your special offer, unlocked.
          </h1>
          <p className="mt-4 text-lg text-zinc-600">
            Thanks for joining the webinar. Pick the plan that fits — both keep you rendering
            without a watermark from day one.
          </p>
        </div>

        {loading && (
          <div className="mt-12 flex justify-center">
            <CircleNotch className="animate-spin text-[#E91E8C]" size={32} />
          </div>
        )}

        {!loading && promo && (
          <>
            {/* Anonymous email field */}
            {!localStorage.getItem("session_token") && (
              <div className="mt-10 max-w-md mx-auto">
                <label htmlFor="webinar-email" className="block text-sm font-medium text-zinc-700 mb-1.5">
                  Email (optional — we'll use it for your receipt)
                </label>
                <input
                  id="webinar-email"
                  data-testid="webinar-email-input"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-zinc-300 focus:border-[#E91E8C] focus:ring-2 focus:ring-[#E91E8C]/20 outline-none text-sm"
                />
              </div>
            )}

            <div className="mt-10 grid sm:grid-cols-2 gap-6">
              {/* Monthly card */}
              <div
                className="bg-white rounded-2xl border-2 border-zinc-200 p-6 sm:p-8 shadow-sm hover:shadow-md transition"
                data-testid="webinar-monthly-card"
              >
                <div className="text-sm font-semibold uppercase tracking-wide text-zinc-500">
                  Starter · Monthly
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="text-5xl font-bold text-zinc-900">{promo.monthly.promo_price}</span>
                  <span className="text-zinc-500 text-sm">first month</span>
                </div>
                <div className="mt-1 text-sm text-zinc-500">
                  then {promo.monthly.regular_price}
                </div>
                <p className="mt-3 text-sm text-zinc-600">{promo.monthly.sub}</p>

                <button
                  onClick={() => claim("monthly")}
                  disabled={claiming !== null}
                  data-testid="webinar-claim-monthly-btn"
                  className="mt-6 w-full bg-[#E91E8C] hover:bg-[#D01880] disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition inline-flex items-center justify-center gap-2"
                >
                  {claiming === "monthly" ? (
                    <><CircleNotch className="animate-spin" size={18} /> Redirecting…</>
                  ) : (
                    "Claim $29 offer"
                  )}
                </button>

                <button
                  onClick={() => copyCode(promo.monthly.code)}
                  data-testid="webinar-copy-monthly-code"
                  className="mt-3 w-full text-xs text-zinc-500 hover:text-[#E91E8C] inline-flex items-center justify-center gap-1.5"
                >
                  <Copy size={12} /> Or use code <span className="font-mono font-semibold">{promo.monthly.code}</span>
                </button>
              </div>

              {/* Yearly card (best value) */}
              <div
                className="relative bg-white rounded-2xl border-2 border-[#E91E8C] p-6 sm:p-8 shadow-md"
                data-testid="webinar-yearly-card"
              >
                <span className="absolute -top-3 left-6 bg-[#E91E8C] text-white text-xs font-bold px-3 py-1 rounded-full">
                  Best value · Save $119
                </span>
                <div className="text-sm font-semibold uppercase tracking-wide text-[#E91E8C]">
                  Starter · Annual
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="text-5xl font-bold text-zinc-900">{promo.yearly.promo_price}</span>
                  <span className="text-zinc-500 text-sm">first year</span>
                </div>
                <div className="mt-1 text-sm text-zinc-500">
                  then {promo.yearly.regular_price}
                </div>
                <p className="mt-3 text-sm text-zinc-600">{promo.yearly.sub}</p>

                <button
                  onClick={() => claim("yearly")}
                  disabled={claiming !== null}
                  data-testid="webinar-claim-yearly-btn"
                  className="mt-6 w-full bg-[#E91E8C] hover:bg-[#D01880] disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition inline-flex items-center justify-center gap-2"
                >
                  {claiming === "yearly" ? (
                    <><CircleNotch className="animate-spin" size={18} /> Redirecting…</>
                  ) : (
                    "Claim $349 offer"
                  )}
                </button>

                <button
                  onClick={() => copyCode(promo.yearly.code)}
                  data-testid="webinar-copy-yearly-code"
                  className="mt-3 w-full text-xs text-zinc-500 hover:text-[#E91E8C] inline-flex items-center justify-center gap-1.5"
                >
                  <Copy size={12} /> Or use code <span className="font-mono font-semibold">{promo.yearly.code}</span>
                </button>
              </div>
            </div>

            <div className="mt-12 max-w-2xl mx-auto bg-white rounded-2xl border border-zinc-200 p-6 sm:p-8">
              <h3 className="text-lg font-semibold text-zinc-900">What you get</h3>
              <ul className="mt-4 space-y-2.5">
                {PERKS.map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-sm text-zinc-700">
                    <Check size={18} weight="bold" className="text-[#E91E8C] mt-0.5 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            <p className="mt-8 text-center text-xs text-zinc-500 max-w-md mx-auto">
              Both offers renew at the regular Starter rate after the promotional period. You can
              downgrade or cancel from Account Settings at any time.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
